import _ from 'underscore';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';

import { getStyles } from './get-styles';
import { LocationTargetEditCallout } from './location-target-edit-callout';
import { BingMapsSearchBoxWithSearchOptions } from '../bing-maps-search-box/index';
import { LocationTargetsGrid } from '../location-targets-grid/index';
import { BingMapsWithZoomButton } from '../bing-maps-view/index';
import {
  getIsBoundaryTarget as defaultGetIsBoundaryTarget,
  getIsRadiusTarget as defaultGetIsRadiusTarget,
  LocationTarget,
  TARGETING_TYPE,
} from '../util/index';

function LocationTargetsEditorUnstyled(props) {
  const {
    quickLocationTargets,
    allowNegativeLocationTargeting,
    allowPositiveLocationTargeting,
    allowBoundaryLocationSearch,
    isMixedLocationSearch,
    classes,
    defaultRadiusSize,
    defaultRadiusUnit,
    getIsBoundaryTarget,
    getIsRadiusTarget,
    locationTargets,
    mapRef,
    maxRadiusInKilometers,
    maxRadiusInMiles,
    negativeLocationTargets,
    onLocationTargetsChange,
    onNegativeLocationTargetsChange,
    searchChangeDebounceWait,
    onLocationTargetsGetErrorMessage,
    onNegativeLocationTargetsGetErrorMessage,
    onSingleLocationTargetGetErrorMessage,
    maxLocationTargets,
    maxNegativeLocationTargets,
    strings,
    isReadonly,
    searchResultsFooter,
    searchResultsFooterOnClick,
    isLoading,
    isRadiusExcludeEnabled,
    customizedLocationEqual,
    defaultSelectedSearchType,
    showSearchTypeOptions,
    helpPopups,
    userRegion,
    locale,
    showExcludeButtonIgnoreLocationId,
    onBulkLocationButtonClick,
  } = props;

  const i18n = useContext(I18nContext);

  const [calloutTargetElement, setCalloutTargetElement] = useState(null);
  const [calloutLocationTarget, setCalloutLocationTarget] = useState(null);
  const [locationTargetsSnapshot, setLocationTargetsSnapshot] = useState([]);
  const [quickTargetingSelected, setQuickTargetingSelected] = useState(false);

  const onLocationTargetBlur = useCallback((event = null) => {
    if (mapRef && mapRef.current) {
      mapRef.current.hideTooltip(event);
    }
  }, [mapRef]);

  const onLocationTargetFocus = useCallback((event, locationTarget) => {
    if (mapRef && mapRef.current) {
      mapRef.current.showTooltip(event, locationTarget);
    }
  }, [mapRef]);

  const onLocationTargetEditOpen = useCallback((event, locationTarget) => {
    setCalloutTargetElement(event.target);
    setCalloutLocationTarget(locationTarget);
  }, []);

  const onLocationTargetEditDismiss = () => {
    setCalloutTargetElement(null);
    setCalloutLocationTarget(null);
  };

  const handleLocationTargetsChange = useCallback((targets) => {
    onLocationTargetBlur();
    onLocationTargetsChange(targets);
  }, [onLocationTargetsChange, onLocationTargetBlur]);

  const onQuickTargetingSelected = useCallback((isQuickTargetingSelected) => {
    if (isQuickTargetingSelected) {
      // 1. switch from specific/radius to quick, show read-only map, snapshot location targets
      setQuickTargetingSelected(true);
      setLocationTargetsSnapshot(locationTargets);
      onLocationTargetsChange(quickLocationTargets);
    } else if (quickTargetingSelected) {
      // 2. switch from quick to specific/radius, restore location targets
      setQuickTargetingSelected(false);
      onLocationTargetsChange(_.isEmpty(locationTargetsSnapshot) ? quickLocationTargets : locationTargetsSnapshot);
    }
    // 3. switch between specific and radius, do nothing
  }, [locationTargets, locationTargetsSnapshot, onLocationTargetsChange, quickLocationTargets, quickTargetingSelected]);

  const handleNegativeLocationTargetsChange = useCallback((targets) => {
    onLocationTargetBlur();
    onNegativeLocationTargetsChange(targets);
  }, [onNegativeLocationTargetsChange, onLocationTargetBlur]);

  const onLocationTargetEditSave = (_isRadiusLocationTarget, radiusSize, radiusUnit) => {
    let onChange = null;
    const newLocationTargets = [];

    // create a new array of location targets
    if (calloutLocationTarget.targetingType === TARGETING_TYPE.Included) {
      onChange = handleLocationTargetsChange;
      newLocationTargets.push(...locationTargets);
    } else {
      onChange = handleNegativeLocationTargetsChange;
      newLocationTargets.push(...negativeLocationTargets);
    }

    // replace the location target at index and set the new array
    const index = _.indexOf(newLocationTargets, calloutLocationTarget);
    if (index >= 0) {
      newLocationTargets[index] = LocationTarget.CreateFromExistingLocationTarget(
        calloutLocationTarget,
        radiusSize,
        radiusUnit
      );

      onChange(newLocationTargets);
    }

    // dismiss
    onLocationTargetEditDismiss();
  };

  const isIncludeNewLocationTargetButtonEnabled = useMemo(
    () => _.size(locationTargets) < maxLocationTargets,
    [locationTargets, maxLocationTargets]
  );

  const isExcludeNewLocationTargetButtonEnabled = useMemo(
    () => _.size(negativeLocationTargets) < maxNegativeLocationTargets,
    [negativeLocationTargets, maxNegativeLocationTargets]
  );

  const hideRemoveAndEditButton = quickTargetingSelected || isReadonly;
  const onLocationTargetEditClick = useMemo(() => (hideRemoveAndEditButton || !showSearchTypeOptions ? null : onLocationTargetEditOpen), [hideRemoveAndEditButton, onLocationTargetEditOpen, showSearchTypeOptions]);

  return (
    <div className={`bing-maps-location-targets-editor  ${classes.bingMapsLocationTargetsEditor}`}>
      {
        !isReadonly && <BingMapsSearchBoxWithSearchOptions
          allowQuickLocationTargeting={!!quickLocationTargets}
          allowPositiveLocationTargeting={allowPositiveLocationTargeting}
          allowNegativeLocationTargeting={allowNegativeLocationTargeting}
          allowBoundaryLocationSearch={allowBoundaryLocationSearch}
          isMixedLocationSearch={isMixedLocationSearch}
          className="bing-maps-location-targets-search-box-with-search-options"
          getIsSearchResultBoundaryTarget={getIsBoundaryTarget}
          getIsSearchResultRadiusTarget={getIsRadiusTarget}
          locationTargets={locationTargets}
          negativeLocationTargets={negativeLocationTargets}
          changeDebounceWait={searchChangeDebounceWait}
          onLocationTargetsChange={handleLocationTargetsChange}
          onNegativeLocationTargetsChange={handleNegativeLocationTargetsChange}
          onQuickTargetingSelected={onQuickTargetingSelected}
          radiusSize={defaultRadiusSize}
          radiusUnit={defaultRadiusUnit}
          isIncludeEnabled={isIncludeNewLocationTargetButtonEnabled}
          isExcludeEnabled={isExcludeNewLocationTargetButtonEnabled}
          isRadiusExcludeEnabled={isRadiusExcludeEnabled}
          strings={strings}
          searchResultsFooter={searchResultsFooter}
          searchResultsFooterOnClick={searchResultsFooterOnClick}
          customizedLocationEqual={customizedLocationEqual}
          showSearchTypeOptions={showSearchTypeOptions}
          defaultSelectedSearchType={defaultSelectedSearchType}
          isLoading={isLoading}
          helpPopups={helpPopups}
          userRegion={userRegion}
          locale={locale}
          showExcludeButtonIgnoreLocationId={showExcludeButtonIgnoreLocationId}
          onBulkLocationButtonClick={onBulkLocationButtonClick}
        />
      }
      <div className="bing-maps-location-targets-search-grid-and-map">
        <div className="bing-maps-location-targets-search-grid">
          {_.isEmpty(locationTargets) && _.isEmpty(negativeLocationTargets) &&
            <div className="bing-maps-location-targets-empty">
              {i18n.getString(_TL_('There are no location targets defined.'))}
            </div>
          }

          { /* Callout control for editing a location target */}
          {calloutTargetElement && calloutLocationTarget &&
            <LocationTargetEditCallout
              className={`bing-maps-location-target-edit-callout ${classes.locationTargetEditCallout}`}
              isBoundaryChoiceEnabled={getIsBoundaryTarget(
                calloutLocationTarget.location,
                calloutLocationTarget.targetingType
              )}
              isRadiusChoiceEnabled={getIsRadiusTarget(
                calloutLocationTarget.location,
                calloutLocationTarget.targetingType
              )}
              isRadiusLocationTarget={calloutLocationTarget.isRadiusLocationTarget}
              locationName={calloutLocationTarget.ExpandedName}
              maxRadiusInKilometers={maxRadiusInKilometers}
              maxRadiusInMiles={maxRadiusInMiles}
              onDismiss={onLocationTargetEditDismiss}
              onSave={onLocationTargetEditSave}
              radiusSize={calloutLocationTarget.radiusSize || defaultRadiusSize}
              radiusUnit={calloutLocationTarget.radiusUnit || defaultRadiusUnit}
              targetElement={calloutTargetElement}
            />
          }

          {allowPositiveLocationTargeting &&
            <div className="bing-maps-location-targets-grid">
              <LocationTargetsGrid
                isNegativeLocationTargetsGrid={false}
                locationTargets={locationTargets}
                onLocationTargetsChange={handleLocationTargetsChange}
                onGetErrorMessage={onLocationTargetsGetErrorMessage}
                onLocationTargetEditClick={onLocationTargetEditClick}
                onLocationTargetGetErrorMessage={onSingleLocationTargetGetErrorMessage}
                onLocationTargetBlur={onLocationTargetBlur}
                onLocationTargetFocus={onLocationTargetFocus}
                hideRemove={hideRemoveAndEditButton}
                isLoading={isLoading}
              />
            </div>
          }

          {allowNegativeLocationTargeting &&
            <div className="bing-maps-negative-location-targets-grid">
              <LocationTargetsGrid
                isNegativeLocationTargetsGrid
                locationTargets={negativeLocationTargets}
                onLocationTargetsChange={handleNegativeLocationTargetsChange}
                onGetErrorMessage={onNegativeLocationTargetsGetErrorMessage}
                onLocationTargetEditClick={onLocationTargetEditClick}
                onLocationTargetGetErrorMessage={onSingleLocationTargetGetErrorMessage}
                hideRemove={hideRemoveAndEditButton}
                isLoading={isLoading}
              />
            </div>
          }
        </div>
        <BingMapsWithZoomButton
          mapRef={mapRef}
          locationTargets={locationTargets}
          negativeLocationTargets={negativeLocationTargets}
        />
      </div>
    </div>
  );
}

LocationTargetsEditorUnstyled.propTypes = {
  quickLocationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTarget)),
  allowNegativeLocationTargeting: PropTypes.bool.isRequired,
  allowPositiveLocationTargeting: PropTypes.bool,
  allowBoundaryLocationSearch: PropTypes.bool,
  isMixedLocationSearch: PropTypes.bool,
  classes: PropTypes.shape({
    bingMapsLocationTargetsEditor: PropTypes.string,
    locationTargetEditCallout: PropTypes.string,
  }).isRequired,
  defaultRadiusSize: PropTypes.number,
  defaultRadiusUnit: PropTypes.oneOf(['mi', 'km']),

  /**
   * Function that receives a location and determines if it is or isn't available for boundary
   * targeting.
   */
  getIsBoundaryTarget: PropTypes.func,

  /**
   * Function that receives a location and determines if it is or isn't available for radius
   * targeting.
   */
  getIsRadiusTarget: PropTypes.func,

  locationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTarget)).isRequired,

  /**
   * Ref that can be used to trigger actions in the map web control like zooming.
   */
  mapRef: PropTypes.shape({
    current: PropTypes.shape({
      hideTooltip: PropTypes.func,
      showTooltip: PropTypes.func,
    }),
  }),

  maxRadiusInKilometers: PropTypes.number,
  maxRadiusInMiles: PropTypes.number,
  negativeLocationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTarget)),
  onLocationTargetsChange: PropTypes.func.isRequired,
  onNegativeLocationTargetsChange: PropTypes.func,
  searchChangeDebounceWait: PropTypes.number,
  onLocationTargetsGetErrorMessage: PropTypes.func,
  onNegativeLocationTargetsGetErrorMessage: PropTypes.func,
  onSingleLocationTargetGetErrorMessage: PropTypes.func,
  maxLocationTargets: PropTypes.number,
  maxNegativeLocationTargets: PropTypes.number,
  strings: PropTypes.shape({}),
  isReadonly: PropTypes.bool,
  searchResultsFooter: PropTypes.func,
  searchResultsFooterOnClick: PropTypes.func,
  isRadiusExcludeEnabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  customizedLocationEqual: PropTypes.func,
  defaultSelectedSearchType: PropTypes.string,
  showSearchTypeOptions: PropTypes.bool,
  helpPopups: PropTypes.shape({}),
  userRegion: PropTypes.string,
  locale: PropTypes.string,
  showExcludeButtonIgnoreLocationId: PropTypes.bool,
  onBulkLocationButtonClick: PropTypes.func,
};

LocationTargetsEditorUnstyled.defaultProps = {
  allowBoundaryLocationSearch: true,
  isMixedLocationSearch: false,
  defaultRadiusSize: 25,
  defaultRadiusUnit: 'mi',
  getIsBoundaryTarget: defaultGetIsBoundaryTarget,
  getIsRadiusTarget: defaultGetIsRadiusTarget,
  mapRef: null,
  maxRadiusInKilometers: 800,
  maxRadiusInMiles: 500,
  negativeLocationTargets: [],
  onNegativeLocationTargetsChange: null,
  searchChangeDebounceWait: 100,
  onLocationTargetsGetErrorMessage: _.noop,
  onNegativeLocationTargetsGetErrorMessage: _.noop,
  onSingleLocationTargetGetErrorMessage: _.noop,
  maxLocationTargets: 25,
  maxNegativeLocationTargets: 25,
  strings: {},
  isReadonly: false,
  searchResultsFooter: null,
  searchResultsFooterOnClick: _.noop,
  isLoading: false,
  isRadiusExcludeEnabled: true,
  quickLocationTargets: undefined,
  allowPositiveLocationTargeting: true,
  customizedLocationEqual: null,
  defaultSelectedSearchType: undefined,
  showSearchTypeOptions: true,
  helpPopups: {},
  userRegion: undefined,
  locale: undefined,
  showExcludeButtonIgnoreLocationId: false,
  onBulkLocationButtonClick: _.noop,
};

export const LocationTargetsEditor = withDefaultStyles(LocationTargetsEditorUnstyled, getStyles);
